<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<!-- Ontology workspace center (design: ims-ontology). Header (title + inline rename, stat pills,
     Projects popup, delete with a guard dialog that blocks deletion while projects are linked),
     a Terms toolbar with search, and the shared OntologyTree (drag/edit/delete/color reused). -->
<template>
<div class="od">
  <b-loading :is-full-page="false" :active="loading" />
  <b-message v-if="error" type="is-danger" has-icon icon-size="is-small">
    {{$t('unexpected-error-info-message')}}
  </b-message>

  <div v-else-if="!loading" class="od-wrap">
    <!-- header -->
    <div class="od-header">
      <div class="od-titlerow">
        <div class="od-icon">
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="#1e63d0" stroke-width="1.9"><circle cx="6" cy="6" r="2.4"/><circle cx="18" cy="6" r="2.4"/><circle cx="12" cy="18" r="2.4"/><path d="M6 8.4v3.6a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8.4M12 14v1.6"/></svg>
        </div>
        <template v-if="!renaming">
          <h1 class="od-title">{{ ontology.name }}</h1>
          <button v-if="canEdit" class="od-rename-btn" :title="$t('button-rename')" @click="startRename">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
          </button>
        </template>
        <template v-else>
          <input ref="renameInput" class="od-rename-input" v-model="renameValue" @keyup.enter="saveRename" @keyup.esc="cancelRename" />
          <button class="od-btn-primary" @click="saveRename">{{ $t('save') }}</button>
          <button class="od-btn-ghost" @click="cancelRename">{{ $t('button-cancel') }}</button>
        </template>
        <div class="od-spacer"></div>
        <button v-if="canEdit" class="od-delete-btn" @click="deleteOpen = true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
          {{ $t('delete-ontology') }}
        </button>
      </div>

      <div class="od-pills">
        <div class="od-pill">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1e63d0" stroke-width="2"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
          <span><strong>{{ termCount }}</strong> {{ $t('terms') }}</span>
        </div>
        <div class="od-pill">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0f9e92" stroke-width="2"><path d="M5 3v4a2 2 0 0 0 2 2h4M5 9v8a2 2 0 0 0 2 2h4"/><circle cx="17" cy="5" r="2"/><circle cx="17" cy="13" r="2"/><circle cx="13" cy="19" r="2"/></svg>
          <span><strong>{{ depth }}</strong> {{ $t('levels-deep') }}</span>
        </div>
        <div class="od-pill-wrap" v-click-outside="() => projectsOpen = false">
          <button class="od-pill od-pill-btn" :class="{open: projectsOpen}" @click="projectsOpen = !projectsOpen">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1e63d0" stroke-width="2"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/></svg>
            <span>{{ $t('projects') }}</span>
            <span class="od-pill-count">{{ nbProjects }}</span>
            <i class="fas fa-chevron-down od-pill-caret" :class="{open: projectsOpen}"></i>
          </button>
          <div v-if="projectsOpen" class="od-proj-pop">
            <div class="od-proj-head"><span>{{ $t('linked-projects') }}</span><span>{{ nbProjects }}</span></div>
            <div v-if="projects.length" class="od-proj-list">
              <router-link v-for="p in projects" :key="p.id" :to="`/project/${p.id}`" class="od-proj-row">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1e63d0" stroke-width="2"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/></svg>
                <span>{{ p.name }}</span>
              </router-link>
            </div>
            <div v-else-if="nbProjects > 0" class="od-proj-empty">{{ $t('used-in-project-no-access') }}</div>
            <div v-else class="od-proj-empty">{{ $t('not-linked-to-any-project') }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- toolbar -->
    <div class="od-toolbar">
      <span class="od-toolbar-title">{{ $t('terms') }}</span>
      <div class="od-spacer"></div>
      <div class="od-termsearch">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>
        <input v-model="termSearch" :placeholder="$t('search-terms')" />
      </div>
    </div>

    <div v-if="canEdit" class="od-hint">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="2"><circle cx="12" cy="12" r="9"/><line x1="12" y1="11" x2="12" y2="16"/><line x1="12" y1="8" x2="12" y2="8"/></svg>
      {{ $t('drag-term-hint') }}
    </div>

    <div class="od-tree">
      <ontology-tree
        :ontology="fullOntology"
        :allowSelection="false"
        :allowDrag="canEdit"
        :allowEdition="canEdit"
        :allowNew="canEdit"
        :searchString="termSearch"
        @treeChange="refreshTree"
      >
        <template #no-result>
          <em class="has-text-grey">{{ $t('no-term') }}</em>
        </template>
      </ontology-tree>
    </div>
  </div>

  <!-- delete guard dialog -->
  <div v-if="deleteOpen" class="od-modal-scrim" @click="deleteOpen = false">
    <div class="od-modal" @click.stop>
      <template v-if="nbProjects > 0">
        <div class="od-modal-body">
          <div class="od-modal-head">
            <div class="od-modal-ico warn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2.1"><path d="M10.3 3.8 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12" y2="17"/></svg>
            </div>
            <div>
              <div class="od-modal-title">{{ $t('cant-delete-ontology') }}</div>
            </div>
          </div>
          <p class="od-modal-text">{{ $t('unlink-projects-first') }}</p>
          <div v-if="projects.length" class="od-modal-projects">
            <div v-for="p in projects" :key="p.id" class="od-proj-row static">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1e63d0" stroke-width="2"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/></svg>
              <span>{{ p.name }}</span>
            </div>
          </div>
        </div>
        <div class="od-modal-foot">
          <button class="od-btn-dark" @click="deleteOpen = false">{{ $t('got-it') }}</button>
        </div>
      </template>
      <template v-else>
        <div class="od-modal-body">
          <div class="od-modal-head">
            <div class="od-modal-ico danger">
              <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.1"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
            </div>
            <div>
              <div class="od-modal-title">{{ $t('delete-ontology') }}?</div>
            </div>
          </div>
          <p class="od-modal-text">{{ $t('delete-ontology-confirm') }}</p>
        </div>
        <div class="od-modal-foot">
          <button class="od-btn-ghost" @click="deleteOpen = false">{{ $t('button-cancel') }}</button>
          <button class="od-btn-danger" @click="confirmDelete">{{ $t('delete-ontology') }}</button>
        </div>
      </template>
    </div>
  </div>
</div>
</template>

<script>
import {get} from '@/utils/store-helpers';
import {Ontology, ProjectCollection} from '@/api';
import OntologyTree from './OntologyTree';

export default {
  name: 'ontology-details',
  props: {
    ontology: Object
  },
  components: {
    OntologyTree
  },
  data() {
    return {
      loading: true,
      error: false,
      fullOntology: {},
      projects: [],
      managedProjects: [],
      termSearch: '',
      projectsOpen: false,
      renaming: false,
      renameValue: '',
      deleteOpen: false
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    canEdit() {
      return !this.currentUser.guestByNow && (this.currentUser.adminByNow || this.currentUser.id === this.fullOntology.user ||
        (this.hasAccessToAllProjects && (this.allProjectsAreCollaborative || this.manageAllProjects)));
    },
    nbProjects() {
      return (this.fullOntology.projects && this.fullOntology.projects.length) || 0;
    },
    hasAccessToAllProjects() {
      return this.nbProjects === this.projects.length;
    },
    allProjectsAreCollaborative() {
      return !this.projects.some(p => p.isRestricted || p.isReadOnly);
    },
    manageAllProjects() {
      return !this.projects.some(p => !this.managedProjects.find(mp => mp.id === p.id));
    },
    topTerms() {
      return (this.fullOntology.children && this.fullOntology.children.array) || [];
    },
    termCount() {
      return this.countTerms(this.topTerms);
    },
    depth() {
      return this.topTerms.length ? this.depthOf(this.topTerms) : 0;
    }
  },
  watch: {
    ontology() {
      this.fetchOntology();
    }
  },
  methods: {
    countTerms(terms) {
      return terms.reduce((sum, t) => sum + 1 + (t.children && t.children.length ? this.countTerms(t.children) : 0), 0);
    },
    depthOf(terms) {
      let max = 1;
      terms.forEach(t => {
        if (t.children && t.children.length) {
          max = Math.max(max, 1 + this.depthOf(t.children));
        }
      });
      return max;
    },
    // Re-fetch the ontology after a term move/create/delete so the tree reflects what the
    // backend actually persisted and the stat pills (Terms / levels deep) recompute. Quiet:
    // no full-page spinner, since this fires on each drag-drop.
    async refreshTree() {
      try {
        this.fullOntology = await Ontology.fetch(this.ontology.id);
      } catch (error) {
        console.log(error);
      }
    },
    async fetchOntology() {
      this.loading = true;
      this.error = false;
      this.projectsOpen = false;
      this.renaming = false;
      this.deleteOpen = false;
      try {
        this.fullOntology = await Ontology.fetch(this.ontology.id);
        // ontology.projects includes projects the current user can't see → refetch the accessible ones
        this.projects = (await ProjectCollection.fetchAll({filterKey: 'ontology', filterValue: this.ontology.id})).array;
      } catch (error) {
        console.log(error);
        this.error = true;
      }
      this.fetchManagedProjects();
      this.loading = false;
    },
    async fetchManagedProjects() {
      this.managedProjects = (await ProjectCollection.fetchAll({
        filterKey: 'user',
        filterValue: this.currentUser.id,
        light: true,
        admin: true
      })).array;
    },
    startRename() {
      this.renameValue = this.ontology.name;
      this.renaming = true;
      this.$nextTick(() => {
        if (this.$refs.renameInput) {
          this.$refs.renameInput.focus();
        }
      });
    },
    cancelRename() {
      this.renaming = false;
    },
    async saveRename() {
      let newName = (this.renameValue || '').trim();
      if (!newName || newName === this.ontology.name) {
        this.renaming = false;
        return;
      }
      let oldName = this.ontology.name;
      try {
        this.fullOntology.name = newName;
        await this.fullOntology.save();
        this.$notify({type: 'success', text: this.$t('notif-success-ontology-rename', {name: newName})});
        this.$emit('rename', newName);
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-ontology-rename', {name: oldName})});
      }
      this.renaming = false;
    },
    confirmDelete() {
      this.deleteOpen = false;
      this.$emit('delete');
    }
  },
  created() {
    this.fetchOntology();
  }
};
</script>

<style lang="scss" scoped>
.od { position: relative; display: flex; flex-direction: column; height: 100%; min-height: 0; }
.od-wrap { display: flex; flex-direction: column; min-height: 0; flex: 1; }

/* header */
.od-header { padding: 22px 32px 16px; border-bottom: 1px solid #f1f4f8; }
.od-titlerow { display: flex; align-items: center; gap: 13px; min-height: 40px; }
.od-icon {
  width: 40px; height: 40px; border-radius: 11px; flex: 0 0 40px;
  background: linear-gradient(135deg, #e8f7f4, #eaf1ff);
  display: flex; align-items: center; justify-content: center;
}
.od-title { font-size: 23px; font-weight: 800; margin: 0; letter-spacing: -0.4px; color: #0f172a; }
.od-rename-btn {
  width: 30px; height: 30px; border-radius: 8px; border: 1px solid transparent;
  background: transparent; color: #94a3b8; cursor: pointer; display: flex; align-items: center; justify-content: center;
}
.od-rename-btn:hover { background: #f1f5f9; color: #1e63d0; }
.od-rename-input {
  font-size: 21px; font-weight: 800; padding: 5px 12px; border: 2px solid #1e63d0;
  border-radius: 9px; outline: none; color: #0f172a; min-width: 260px;
}
.od-spacer { flex: 1; }
.od-btn-primary { height: 38px; padding: 0 16px; border: none; border-radius: 9px; background: #1e63d0; color: #fff; font-weight: 700; font-size: 14px; cursor: pointer; }
.od-btn-ghost { height: 38px; padding: 0 14px; border: 1px solid #e2e8f0; border-radius: 9px; background: #fff; color: #475569; font-weight: 600; font-size: 14px; cursor: pointer; }
.od-btn-ghost:hover { background: #f1f5f9; }
.od-delete-btn {
  height: 38px; padding: 0 15px; border: 1px solid #fde0e0; border-radius: 9px; background: #fff;
  color: #ef4444; font-weight: 600; font-size: 13.5px; cursor: pointer; display: flex; align-items: center; gap: 7px;
}
.od-delete-btn:hover { background: #fef2f2; border-color: #fca5a5; }

/* stat pills */
.od-pills { display: flex; gap: 10px; margin-top: 16px; flex-wrap: wrap; align-items: center; }
.od-pill {
  display: flex; align-items: center; gap: 8px; height: 34px; padding: 0 13px;
  background: #f7f9fc; border: 1px solid #eef1f6; border-radius: 9px; font-size: 13.5px; color: #475569; font-weight: 600;
}
.od-pill strong { color: #0f172a; }
.od-pill-wrap { position: relative; }
.od-pill-btn { cursor: pointer; }
.od-pill-btn.open, .od-pill-btn:hover { background: #eef4ff; border-color: #dbe6fb; }
.od-pill-count { background: #1e63d0; color: #fff; font-size: 11px; font-weight: 800; min-width: 18px; height: 18px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; padding: 0 5px; }
.od-pill-caret { font-size: 10px; color: #94a3b8; transition: transform 0.15s; }
.od-pill-caret.open { transform: rotate(180deg); }
.od-proj-pop {
  position: absolute; top: 42px; left: 0; width: 280px; background: #fff; border: 1px solid #e9edf3;
  border-radius: 12px; box-shadow: 0 18px 44px -14px rgba(15, 23, 42, 0.32); z-index: 40; overflow: hidden;
}
.od-proj-head { display: flex; align-items: center; justify-content: space-between; padding: 12px 15px; border-bottom: 1px solid #f1f4f8; background: #fafbfd; font-size: 12px; font-weight: 700; letter-spacing: 0.6px; color: #94a3b8; text-transform: uppercase; }
.od-proj-list { padding: 6px; max-height: 260px; overflow-y: auto; }
.od-proj-row { display: flex; align-items: center; gap: 10px; padding: 9px 11px; border-radius: 9px; font-size: 14px; font-weight: 600; color: #1e293b; text-decoration: none; }
.od-proj-row:hover { background: #f1f5fb; color: #1e63d0; }
.od-proj-row.static:hover { background: transparent; color: #1e293b; }
.od-proj-empty { padding: 22px 16px; text-align: center; font-size: 13.5px; color: #94a3b8; }

/* toolbar */
.od-toolbar { display: flex; align-items: center; gap: 12px; padding: 16px 32px 12px; flex-wrap: wrap; }
.od-toolbar-title { font-size: 15px; font-weight: 700; color: #0f172a; }
.od-termsearch { display: flex; align-items: center; gap: 8px; height: 36px; background: #fff; border: 1px solid #e9edf3; border-radius: 9px; padding: 0 11px; width: 240px; max-width: 40vw; }
.od-termsearch input { border: none; background: transparent; outline: none; font-size: 13.5px; width: 100%; color: #334155; }
.od-hint { margin: 0 32px 6px; display: flex; align-items: center; gap: 9px; font-size: 12.5px; color: #94a3b8; }

/* tree */
.od-tree { flex: 1; overflow-y: auto; padding: 4px 32px 28px; min-height: 0; }
.od-tree ::v-deep .ontology-tree { max-width: 1080px; }

/* delete dialog */
.od-modal-scrim { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.55); z-index: 65; display: flex; align-items: center; justify-content: center; padding: 24px; }
.od-modal { width: 480px; max-width: 100%; background: #fff; border-radius: 15px; overflow: hidden; box-shadow: 0 30px 80px -20px rgba(15, 23, 42, 0.45); }
.od-modal-body { padding: 24px 26px 20px; }
.od-modal-head { display: flex; align-items: center; gap: 14px; }
.od-modal-ico { width: 46px; height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex: 0 0 46px; }
.od-modal-ico.warn { background: #fef3c7; }
.od-modal-ico.danger { background: #fee2e2; }
.od-modal-title { font-size: 18px; font-weight: 800; color: #0f172a; }
.od-modal-text { font-size: 14.5px; line-height: 1.55; color: #475569; margin: 18px 0 0; }
.od-modal-projects { margin-top: 14px; background: #fafbfd; border: 1px solid #eef1f6; border-radius: 11px; padding: 6px; }
.od-modal-foot { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 26px; border-top: 1px solid #eef1f5; background: #f7f9fc; }
.od-btn-dark { height: 42px; padding: 0 24px; border: none; border-radius: 10px; background: #0f172a; color: #fff; font-weight: 700; font-size: 14px; cursor: pointer; }
.od-btn-dark:hover { background: #1e293b; }
.od-btn-danger { height: 42px; padding: 0 22px; border: none; border-radius: 10px; background: #ef4444; color: #fff; font-weight: 700; font-size: 14px; cursor: pointer; }
.od-btn-danger:hover { background: #dc2626; }
</style>
